import { useMemo } from 'react'

import { Element } from 'react-scroll'

import { useBlocker } from '@/shared/hooks/useBlocker'
import type { JourneyWithUnderscores, StageWithUnderscores } from '@/types/journey'

import {
  getConnectorToneFromStatus,
  LevelTimelineDot,
  PhaseTimelineDot,
  TimelineRow,
  type TimelineConnectorTone
} from '../components/JourneyTimeline'
import { PhaseCard } from '../components/PhaseCard'
import { LevelCard } from '../Levels/Card'
import { getLevelUiStatus } from '../lib/levelUiStatus'
import { getPhaseUiStatus, isStageUnlocked } from '../lib/phaseUiStatus'

import type { LevelUiStatus } from '../constants/status'


interface StageCardProps {
  stage: StageWithUnderscores
  stageIndex: number
  stageIsUnique: boolean
  journey: JourneyWithUnderscores
  isOpen: boolean
  onToggle: () => void
  showConnector: boolean
  isFirstStage?: boolean
  previousStage?: StageWithUnderscores
  previousStageIndex?: number
}

function getStageEndConnectorTone(
  stage: StageWithUnderscores,
  journey: JourneyWithUnderscores,
  checkBlockCondition: ReturnType<typeof useBlocker>['checkBlockCondition']
): TimelineConnectorTone {
  const lastLevel = stage._levels[stage._levels.length - 1]

  if (lastLevel) {
    const blockResult = checkBlockCondition(lastLevel._blockCondition)
    const status = getLevelUiStatus({
      level: lastLevel,
      blockResult,
      currentLevelUid: journey._currentLevel?.uid
    })

    return getConnectorToneFromStatus(status)
  }

  return getConnectorToneFromStatus(
    getPhaseUiStatus({
      stage,
      isStageUnlocked: isStageUnlocked(stage, checkBlockCondition),
      currentLevelUid: journey._currentLevel?.uid
    })
  )
}

export const DigitalProductJourneyStageCard = ({
  stage,
  stageIndex,
  stageIsUnique,
  journey,
  isOpen,
  onToggle,
  showConnector,
  isFirstStage = true,
  previousStage,
  previousStageIndex
}: StageCardProps) => {
  const { checkBlockCondition } = useBlocker()
  const hasSequentialLevels = isOpen && !stage.is_open && stage._levels.length > 0
  const stageUnlocked = isStageUnlocked(stage, checkBlockCondition)

  const phaseUiStatus = getPhaseUiStatus({
    stage,
    previousStage,
    isStageUnlocked: stageUnlocked,
    currentLevelUid: journey._currentLevel?.uid
  })

  const phaseConnectorTone = getConnectorToneFromStatus(phaseUiStatus)

  /* Fase parcialmente concluída pinta toda a trilha de níveis com o próprio
     tom, em vez de cada nível independente — nível bloqueado nunca herda esse
     tom (ver `toneForLevel`). Fase `success`/`available`/`locked` não aplica:
     só existe nível `success` dentro de fase `progress` (ver `phaseUiStatus`). */
  const phaseTone: TimelineConnectorTone | undefined =
    phaseUiStatus === 'progress' ? 'progress' : undefined

  const toneForLevel = (levelUiStatus: LevelUiStatus): TimelineConnectorTone =>
    levelUiStatus === 'locked'
      ? 'locked'
      : (phaseTone ?? getConnectorToneFromStatus(levelUiStatus))

  const phaseConnectorPreTone = useMemo(() => {
    if (isFirstStage || !previousStage) return undefined

    return getStageEndConnectorTone(previousStage, journey, checkBlockCondition)
  }, [checkBlockCondition, isFirstStage, journey, previousStage])

  const phaseConnectorPreGradient =
    phaseConnectorPreTone === 'success' && phaseUiStatus === 'progress'

  const renderLevelRow = (
    level: StageWithUnderscores['_levels'][number],
    index: number,
    levels: StageWithUnderscores['_levels'],
    options?: { showStageConnector?: boolean; bridgeGapAbove?: boolean }
  ) => {
    const blockResult = checkBlockCondition(level._blockCondition)
    const uiStatus = getLevelUiStatus({
      level,
      blockResult,
      currentLevelUid: journey._currentLevel?.uid
    })
    const isLastLevel = index === levels.length - 1
    const levelConnectorTone = toneForLevel(uiStatus)
    const previousLevel = index > 0 ? levels[index - 1] : null

    const connectorPreTone =
      index === 0
        ? phaseConnectorTone
        : previousLevel
          ? toneForLevel(
              getLevelUiStatus({
                level: previousLevel,
                blockResult: checkBlockCondition(previousLevel._blockCondition),
                currentLevelUid: journey._currentLevel?.uid
              })
            )
          : phaseConnectorTone

    const connectorPreGradient =
      connectorPreTone === 'success' && uiStatus === 'progress'

    return (
      <TimelineRow
        key={level.uid}
        dotSize="level"
        dot={
          <LevelTimelineDot
            status={uiStatus}
            phaseTone={uiStatus === 'locked' ? undefined : phaseTone}
          />
        }
        showConnectorPre
        connectorPreBridge={index === 0 && options?.bridgeGapAbove}
        connectorPreTone={connectorPreTone}
        connectorPreGradient={connectorPreGradient}
        showConnector={
          options?.showStageConnector ?? (!isLastLevel || showConnector)
        }
        connectorTone={levelConnectorTone}
      >
        <Element name={`level-${level.uid}`} id={`level-${level.uid}`}>
          <LevelCard journey={journey} level={level} />
        </Element>
      </TimelineRow>
    )
  }

  if (stageIsUnique) {
    return (
      <div className="flex flex-col">
        {/* Mesmo respiro fase→níveis do caminho com TimelineRow (`mb-5`) —
            sem este wrapper, fase única colava no primeiro nível. */}
        <div className="mb-5">
          <PhaseCard
            stage={stage}
            stageIndex={stageIndex}
            uiStatus={phaseUiStatus}
          />
        </div>
        {isOpen &&
          (stage.is_open ? (
            <div className="flex flex-col gap-3 mb-1 px-1">
              {stage._levels.map(level => (
                <Element
                  key={level.uid}
                  name={`level-${level.uid}`}
                  id={`level-${level.uid}`}
                >
                  <LevelCard journey={journey} level={level} />
                </Element>
              ))}
            </div>
          ) : (
            stage._levels.map((level, index) =>
              renderLevelRow(level, index, stage._levels, {
                showStageConnector: index < stage._levels.length - 1,
                bridgeGapAbove: true
              })
            )
          ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <TimelineRow
        dotSize="phase"
        dot={
          <PhaseTimelineDot
            uiStatus={phaseUiStatus}
            stageIndex={stageIndex}
            isOpen={isOpen}
            onToggle={onToggle}
          />
        }
        showConnectorPre={!isFirstStage}
        connectorPreTone={phaseConnectorPreTone}
        connectorPreGradient={phaseConnectorPreGradient}
        showConnector={hasSequentialLevels || showConnector}
        connectorTone={phaseConnectorTone}
      >
        <PhaseCard
          stage={stage}
          stageIndex={stageIndex}
          previousStageIndex={previousStageIndex}
          uiStatus={phaseUiStatus}
        />
      </TimelineRow>

      {hasSequentialLevels &&
        stage._levels.map((level, index) =>
          renderLevelRow(level, index, stage._levels)
        )}

      {isOpen && stage.is_open && (
        <div className="ml-[calc(36px+0.75rem)] pb-3">
          <div className="flex flex-col gap-3 mb-1 px-1">
            {stage._levels.map(level => (
              <Element
                key={level.uid}
                name={`level-${level.uid}`}
                id={`level-${level.uid}`}
              >
                <LevelCard journey={journey} level={level} />
              </Element>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
